import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Mutation } from 'react-apollo';
import cn from 'classnames';
import { push } from 'connected-react-router';
import { List } from 'immutable';
import memoize from 'lodash.memoize';

import turfBbox from '@turf/bbox';
import turfIntersect from '@turf/intersect';

import { banner } from 'actions';

import { FETCH_ALL_FIELDS, MUTATION_CREATE_FIELDS } from 'api/field';
import { BANNER_IDS, ROUTE_FIELDS } from 'common/constants';
import { Checkbox } from 'common/components/Checkbox';
import {
  bboxToBounds,
  coordsToLatLng,
} from 'common/components/MapComponent/util';
import { Button } from 'common/components';

import {
  DEFAULT_AREA_THRESHOLD,
  DEFAULT_FEATURES,
  DEFAULT_IMPORT_ERROR,
  MAX_FEATURES_ON_MAP,
} from '.';
import ThresholdControl from './ThresholdControl';
import ThresholdReadout from './ThresholdReadout';

const DEFAULT_AREA_THRESHOLD_EXPANDED = false;
const DEFAULT_SUBMISSION_ERROR = false;

// Clamp a value to between a minimum and maximum
const clamp = (val, min, max) => Math.min(max, Math.max(min, val));

// Decide the upper bound of the threshold range slider based on the biggest
// loaded feature
const maxAreaToRangeMax = maxArea => {
  // Minimal number
  if (maxArea < DEFAULT_AREA_THRESHOLD[0]) {
    return DEFAULT_AREA_THRESHOLD[0] * 2;
  }

  // Get rid of decimals
  const roughMax = Math.round(maxArea);

  // Render as string so we can get the number of digits
  const roughMaxStr = roughMax.toString();

  // Get the first digit
  const firstDigit = parseInt(roughMaxStr.substring(0, 1), 10);

  // If the first digit is a 9, go up to the next magnitude
  // eg 922 -> 1000
  if (firstDigit === 9) {
    return Math.pow(10, roughMaxStr.length);
  }

  // Otherwise increment the first digit and zero the rest
  // eg 243 -> 300
  return (firstDigit + 1) * Math.pow(10, roughMaxStr.length - 1);
};

// Memoized function to determine whether two features intersect
const featuresIntersect = memoize(
  (a, b) => turfIntersect(a.geojson, b.geojson) != null,
  (a, b) => [a.id, b.id].sort().join('::')
);

const getCheckedFeatureIntersections = memoize(
  (checkedFeatures, existingFields) => {
    // All existing fields and checked features as a map of identifier to data
    const idToNameAndData = checkedFeatures
      .map(f => f.toJS())
      .merge(
        List(existingFields)
          .toMap()
          .mapEntries(([, f]) => [f.id, f])
      );

    // Calculate intersections between checked features and existing features
    const checkedFeatureIntersections = checkedFeatures.map(feature =>
      idToNameAndData
        .filter(
          (f, id) =>
            id !== feature.get('id') && featuresIntersect(f, feature.toJS())
        )
        .valueSeq()
        .toJS()
    );

    return checkedFeatureIntersections;
  },
  (checkedFeatures, existingFields) =>
    checkedFeatures
      .valueSeq()
      .toJS()
      .concat(existingFields)
      .map(f => f.id)
      .sort()
      .join('\n')
);

const ChooseFieldsForm = ({
  areaInRange,
  areaThreshold,
  dispatch,
  existingFields,
  features,
  filteredFeatures,
  mapRef,
  setAreaThreshold,
  setFeatures,
  setImportError,
}) => {
  const areaThresholdRangeMin = 0;
  const areaThresholdRangeMax = maxAreaToRangeMax(
    features
      .map(feature => feature.get('area'))
      .reduce((acc, area) => Math.max(acc, area), 0)
  );

  const [areaThresholdExpanded, setAreaThresholdExpanded] = useState(
    DEFAULT_AREA_THRESHOLD_EXPANDED
  );
  const clampedAreaThreshold = areaThreshold.map(v =>
    clamp(v, areaThresholdRangeMin, areaThresholdRangeMax)
  );

  const [submissionError, setSubmissionError] = useState(
    DEFAULT_SUBMISSION_ERROR
  );

  // Filtered features
  const checkedFeatures = features.filter(feature => feature.get('checked'));

  // Get intersections between checked features and existing features
  const checkedFeatureIntersections = getCheckedFeatureIntersections(
    checkedFeatures,
    existingFields
  );

  // Set of names of existing fields
  const existingFieldNames = new Set(existingFields.map(field => field.name));

  // Zoom to a feature by ID
  const zoomToFeature = id => {
    if (!mapRef.current) {
      return;
    }
    mapRef.current.fitBounds(
      bboxToBounds(turfBbox(features.getIn([id, 'geojson']).toJS()))
    );
  };

  return (
    <React.Fragment>
      <div className="uploadfield__controls uploadfield__padded">
        <h3 className="text--header text--medium">Select and add fields</h3>
        <fieldset>
          <ThresholdReadout
            areaThresholdExpanded={areaThresholdExpanded}
            clampedAreaThreshold={clampedAreaThreshold}
            setAreaThresholdExpanded={setAreaThresholdExpanded}
          />
          {areaThresholdExpanded && (
            <ThresholdControl
              areaInRange={areaInRange}
              areaThresholdRangeMax={areaThresholdRangeMax}
              areaThresholdRangeMin={areaThresholdRangeMin}
              clampedAreaThreshold={clampedAreaThreshold}
              features={features}
              setAreaThreshold={setAreaThreshold}
              setFeatures={setFeatures}
            />
          )}
        </fieldset>

        <FeatureCountReadouts
          numCheckedFeatures={checkedFeatures.size}
          numFilteredFeatures={filteredFeatures.size}
          numAllFeatures={features.size}
        />
      </div>

      <Mutation
        mutation={MUTATION_CREATE_FIELDS}
        refetchQueries={() => [
          { query: FETCH_ALL_FIELDS, variables: { active: true } },
        ]}
        ignoreResult
        onCompleted={() => {
          dispatch(push(ROUTE_FIELDS));
          dispatch(
            banner.display(
              BANNER_IDS.APP,
              `${checkedFeatures.size} FIELD${
                checkedFeatures.size === 1 ? '' : 'S'
              } ADDED`,
              false
            )
          );
        }}
      >
        {create => {
          const handleSubmit = () => {
            // Clear previous submission error
            setSubmissionError(DEFAULT_SUBMISSION_ERROR);

            // Clear custom validities of fields
            filteredFeatures.slice(0, MAX_FEATURES_ON_MAP).forEach(feature => {
              const li = document.getElementById(feature.get('id'));
              for (const fieldName of ['name', 'area']) {
                li.querySelector(
                  `input[name="${fieldName}"]`
                ).setCustomValidity('');
              }
            });

            // Look for duplicate names within the fields being
            // added
            const nameCounts = {};
            checkedFeatures.forEach(feature => {
              const name = feature.get('name');
              if (!name.length) {
                return;
              }
              if (nameCounts[name] == null) {
                nameCounts[name] = 0;
              }
              nameCounts[name]++;
            });
            for (const [n, c] of Object.entries(nameCounts)) {
              if (c > 1) {
                checkedFeatures
                  .filter(feature => feature.get('name') === n)
                  .forEach(feature => {
                    document
                      .getElementById(feature.get('id'))
                      .querySelector('input[name="name"]')
                      .setCustomValidity('Names must be unique');
                  });
              }
            }

            const ok = checkedFeatures.every(feature => {
              const ul = document.getElementById(feature.get('id'));
              const nameField = ul.querySelector('input[name="name"]');
              const areaField = ul.querySelector('input[name="area"]');

              // Set invalid status on names which are duplicates of
              // existing fields
              if (existingFieldNames.has(feature.get('name'))) {
                nameField.setCustomValidity(
                  'There is an existing field with this name'
                );
              }

              // Check all fields validate
              return [nameField, areaField].every(formField =>
                formField.reportValidity()
              );
            });

            if (!ok) {
              return;
            }

            create({
              variables: {
                fields: checkedFeatures
                  .valueSeq()
                  .toJS()
                  .map(feature => ({
                    name: feature.name,
                    location: coordsToLatLng(
                      feature.point.geometry.coordinates
                    ),
                    size: { size: feature.area, type: 'acres' },
                    geojson: JSON.stringify(feature.geojson),
                  })),
              },
            }).catch(error => {
              setSubmissionError(
                error.message.replace(/^GraphQL error:\s*/, '')
              );
            });
          };

          return (
            <div className="uploadfield__features uploadfield__padded">
              {/* Filtered list of features */}
              <ul>
                {filteredFeatures
                  .slice(0, MAX_FEATURES_ON_MAP)
                  .valueSeq()
                  .toJS()
                  .map(feature => (
                    <li
                      key={feature.id}
                      id={feature.id}
                      className={cn('uploadfield__featurerow', {
                        'is-highlighted': feature.hovering,
                      })}
                      onMouseOver={() => {
                        setFeatures(
                          features.setIn([feature.id, 'hovering'], true)
                        );
                      }}
                      onMouseOut={() => {
                        setFeatures(
                          features.setIn([feature.id, 'hovering'], false)
                        );
                      }}
                    >
                      <div className="uploadfield__featurerow__flex">
                        <Checkbox
                          className="uploadfield__featurerow__checkbox"
                          input={{
                            onChange: event => {
                              setFeatures(
                                features.setIn(
                                  [feature.id, 'checked'],
                                  event.currentTarget.checked
                                )
                              );
                            },
                            value: feature.checked,
                          }}
                          meta={{
                            touched: false,
                            error: null,
                          }}
                        />
                        <input
                          className="uploadfield__featurerow__name"
                          type="text"
                          name="name"
                          value={feature.name}
                          required
                          placeholder="Enter field name"
                          onChange={event => {
                            // Clear any custom validity; it'll be
                            // checked on the next submission attempt
                            event.currentTarget.setCustomValidity('');

                            setFeatures(
                              features.setIn(
                                [feature.id, 'name'],
                                event.currentTarget.value
                              )
                            );
                          }}
                          onFocus={() => {
                            zoomToFeature(feature.id);
                          }}
                          onBlur={event => {
                            setFeatures(
                              features.setIn(
                                [feature.id, 'name'],
                                event.currentTarget.value.trim()
                              )
                            );
                          }}
                        />
                        <span className="uploadfield__featurerow__area">
                          <input
                            className="uploadfield__featurerow__area__input"
                            type="number"
                            name="area"
                            value={feature.area}
                            required
                            step="0.01"
                            min={clampedAreaThreshold[0]}
                            max={clampedAreaThreshold[1]}
                            onChange={event => {
                              setFeatures(
                                features.setIn(
                                  [feature.id, 'area'],
                                  event.currentTarget.value
                                )
                              );
                            }}
                            onFocus={() => {
                              setFeatures(
                                features.setIn(
                                  [feature.id, 'editingArea'],
                                  true
                                )
                              );
                              zoomToFeature(feature.id);
                            }}
                            onBlur={() => {
                              setFeatures(
                                features.setIn(
                                  [feature.id, 'editingArea'],
                                  false
                                )
                              );
                            }}
                          />
                          <small className="uploadfield__featurerow__area__unit">
                            acres
                          </small>
                        </span>
                      </div>
                      {checkedFeatureIntersections.get(feature.id) &&
                      checkedFeatureIntersections.get(feature.id).length ? (
                        <p className="uploadfield__error">
                          This field overlaps with{' '}
                          {checkedFeatureIntersections
                            .get(feature.id)
                            .map(f => f.name || 'unnamed field')
                            .join(', ')}
                        </p>
                      ) : null}
                    </li>
                  ))}
              </ul>
              {filteredFeatures.size > MAX_FEATURES_ON_MAP && (
                <p className="uploadfield__error">
                  Only the largest {MAX_FEATURES_ON_MAP} of the{' '}
                  {filteredFeatures.size} filtered fields are shown. Adjust the
                  size threshold if you can’t find the fields you’re looking
                  for.
                </p>
              )}

              <SubmissionError submissionError={submissionError} />

              {/* Controls */}
              <div className="field__group">
                <fieldset className="field text--center">
                  <Button
                    className="btn btn--primary"
                    onClick={handleSubmit}
                    disabled={checkedFeatures.size === 0}
                  >
                    Add fields
                  </Button>
                </fieldset>
              </div>
              <div className="field__group">
                <fieldset className="field text--center">
                  <Button
                    className="button btn"
                    onClick={() => {
                      setAreaThresholdExpanded(DEFAULT_AREA_THRESHOLD_EXPANDED);
                      setFeatures(DEFAULT_FEATURES);
                      setImportError(DEFAULT_IMPORT_ERROR);
                    }}
                  >
                    Cancel
                  </Button>
                </fieldset>
              </div>
            </div>
          );
        }}
      </Mutation>
    </React.Fragment>
  );
};
ChooseFieldsForm.propTypes = {
  areaThreshold: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  areaInRange: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  existingFields: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  features: PropTypes.object.isRequired,
  filteredFeatures: PropTypes.object.isRequired,
  mapRef: PropTypes.object.isRequired,
  setAreaThreshold: PropTypes.func.isRequired,
  setFeatures: PropTypes.func.isRequired,
  setImportError: PropTypes.func.isRequired,
};
export default ChooseFieldsForm;

const FeatureCountReadouts = ({
  numAllFeatures,
  numCheckedFeatures,
  numFilteredFeatures,
}) => (
  <p>
    {numCheckedFeatures}
    {' of '}
    {numFilteredFeatures}
    {` field${numFilteredFeatures === 1 ? '' : 's'} selected`}
    {numFilteredFeatures < numAllFeatures &&
      `, ${numAllFeatures - numFilteredFeatures} filtered out`}
  </p>
);
FeatureCountReadouts.propTypes = {
  numAllFeatures: PropTypes.number.isRequired,
  numCheckedFeatures: PropTypes.number.isRequired,
  numFilteredFeatures: PropTypes.number.isRequired,
};

const SubmissionError = ({ submissionError }) =>
  submissionError && (
    <p className="text text--centertext--error">{submissionError}</p>
  );



// WEBPACK FOOTER //
// ./src/pages/Field/Upload/ChooseFieldsForm.js